import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Alert, Button, Card, Heading, Loader, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectFirstErrorMessageForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'

import { TAX_ENTITY_TYPES } from '@/features/taxes/taxConstants'
import { TaxfyleQuestionField } from './TaxfyleQuestionField'
import {
  FETCH_TAXFYLE_SHORT_FORM_KEY,
  SAVE_TAXFYLE_SHORT_FORM_KEY,
  SUBMIT_TAXFYLE_SHORT_FORM_KEY,
  TaxfyleShortFormSubmitOutcome,
  fetchTaxfyleShortFormQuestionnaire,
  saveTaxfyleShortFormAnswers,
  submitTaxfyleShortForm,
  type TaxfyleAnswer,
  type TaxfyleShortFormQuestion,
  type TaxfyleShortFormQuestionnaire,
} from './taxfyleShortForm.slice'

type AnswerMap = Record<string, Exclude<TaxfyleAnswer, null>>

const FORM_TYPE_LABELS: Record<string, string> = {
  [TAX_ENTITY_TYPES.form_1040]: 'personal return',
  [TAX_ENTITY_TYPES.form_1120_s]: 'S corporation return',
}

const seedAnswers = (questions: TaxfyleShortFormQuestion[]): AnswerMap =>
  questions.reduce<AnswerMap>((acc, question) => {
    if (question.answer !== null) {
      acc[question.id] = question.answer
    }
    return acc
  }, {})

export const TaxfyleShortForm = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  // This form only ever files a year that has already turned over, so falling
  // back to the current season would ask for a year it will not serve.
  const taxYear = searchParams.get('year') ?? String(Number(currentTaxYear) - 1)
  const [questions, setQuestions] = useState<TaxfyleShortFormQuestion[]>([])
  const [answers, setAnswers] = useState<AnswerMap>({})
  const [formType, setFormType] = useState<string | null>(null)
  // Only set once submit has been attempted, so the form is not red on arrival.
  const [showMissing, setShowMissing] = useState(false)
  const firstMissingRef = useRef<HTMLDivElement | null>(null)
  const [booksLocked, setBooksLocked] = useState(true)
  // Counts this questionnaire too, so more than one means another follows.
  const [outstandingFormCount, setOutstandingFormCount] = useState(1)
  const [loaded, setLoaded] = useState(false)
  const [submitBlockedMessage, setSubmitBlockedMessage] = useState<
    string | null
  >(null)

  const fetchKeys = useMemo(
    () => [
      FETCH_TAXFYLE_SHORT_FORM_KEY,
      SAVE_TAXFYLE_SHORT_FORM_KEY,
      SUBMIT_TAXFYLE_SHORT_FORM_KEY,
    ],
    []
  )
  const isLoading = useReselector(selectIsFetchingForKeys, fetchKeys)
  const error = useReselector(selectFirstErrorMessageForKeys, fetchKeys)

  const load = useCallback(async () => {
    const data = await fetchTaxfyleShortFormQuestionnaire(taxYear)(dispatch)
    if (data) {
      setFormType(data.formType)
      setBooksLocked(data.booksLocked)
      setOutstandingFormCount(data.outstandingFormCount)
      setQuestions(data.questions)
      setAnswers(seedAnswers(data.questions))
    }
    setLoaded(true)
  }, [dispatch, taxYear])

  useEffect(() => {
    load()
  }, [load])

  // Rendered order, so the first entry is the highest one on the page.
  const unansweredQuestionIds = questions
    .filter((question) => answers[question.id] === undefined)
    .map((question) => question.id)
  const unansweredCount = unansweredQuestionIds.length

  const handleSaveAndExit = useCallback(async () => {
    const data = await saveTaxfyleShortFormAnswers(taxYear, answers)(dispatch)
    if (data) {
      navigate('/taxes/archive')
    }
  }, [answers, dispatch, navigate, taxYear])

  const revealMissing = useCallback(() => {
    setShowMissing(true)
    // After paint, so the marked field exists and its position is settled.
    requestAnimationFrame(() => {
      firstMissingRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }, [])

  const showRefreshedQuestions = useCallback(
    (refreshed: TaxfyleShortFormQuestionnaire) => {
      setBooksLocked(refreshed.booksLocked)
      setOutstandingFormCount(refreshed.outstandingFormCount)
      setQuestions(refreshed.questions)
      const refreshedAnswers = seedAnswers(refreshed.questions)
      setAnswers(refreshedAnswers)
      const stillMissing = refreshed.questions.filter(
        (question) => refreshedAnswers[question.id] === undefined
      )
      // Taxfyle decides what counts as answered, so it can refuse a set this
      // form considers complete. Say so rather than marking nothing.
      if (stillMissing.length === 0) {
        setSubmitBlockedMessage(
          'Your tax preparer could not accept these answers yet. Please review them and try again, or reach out to support.'
        )
        return
      }
      revealMissing()
    },
    [revealMissing]
  )

  const handleSubmit = useCallback(async () => {
    setSubmitBlockedMessage(null)
    if (unansweredCount > 0) {
      revealMissing()
      return
    }

    const saved = await saveTaxfyleShortFormAnswers(taxYear, answers)(dispatch)
    if (!saved) {
      return
    }

    // Answers can reveal conditional follow-ups, so show what saving turned up
    // rather than submitting against questions the user has not seen.
    if (!saved.readyToSubmit) {
      showRefreshedQuestions(saved)
      return
    }

    const submitted = await submitTaxfyleShortForm(taxYear)(dispatch)
    if (!submitted) {
      return
    }
    if (submitted.outcome === TaxfyleShortFormSubmitOutcome.needsAnswers) {
      showRefreshedQuestions(submitted.questionnaire)
      return
    }
    // A two-form filer answers a second questionnaire against its own Taxfyle
    // job rather than leaving with the module still open.
    if (submitted.nextFormType) {
      await load()
      return
    }
    navigate('/taxes/archive')
  }, [
    answers,
    dispatch,
    load,
    navigate,
    revealMissing,
    showRefreshedQuestions,
    taxYear,
    unansweredCount,
  ])

  if (!loaded) {
    return <Loader />
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <Heading>
          {taxYear} tax questionnaire
          {formType && FORM_TYPE_LABELS[formType]
            ? `: ${FORM_TYPE_LABELS[formType]}`
            : ''}
        </Heading>
        <Text>
          Your tax preparer needs these answers before they can start on your
          return. There are {questions.length} questions.
        </Text>
      </div>

      {(error || submitBlockedMessage) && (
        <Alert variant="error">{error || submitBlockedMessage}</Alert>
      )}

      <Alert variant="default">
        {booksLocked
          ? 'Please complete the questionnaire below. We will connect you to a tax preparer once it is submitted.'
          : `Please complete the questionnaire below to the best of your knowledge. We will connect you to a tax preparer once your ${taxYear} books are finalized.`}
      </Alert>

      <Card>
        <div className="flex flex-col gap-6 p-6">
          {questions.map((question) => (
            <TaxfyleQuestionField
              key={question.id}
              question={question}
              value={answers[question.id] ?? null}
              missing={
                showMissing && unansweredQuestionIds.includes(question.id)
              }
              fieldRef={
                question.id === unansweredQuestionIds[0]
                  ? firstMissingRef
                  : undefined
              }
              onChange={(value) =>
                setAnswers((current) => {
                  if (value === null) {
                    const next = { ...current }
                    delete next[question.id]
                    return next
                  }
                  return { ...current, [question.id]: value }
                })
              }
            />
          ))}
        </div>
      </Card>

      <div className="flex items-center justify-between gap-4">
        <Text
          variant={TypographyStyles.BODY_SM}
          className={
            showMissing && unansweredCount > 0
              ? 'text-danger'
              : 'text-neutral-600'
          }
        >
          {unansweredCount === 0
            ? 'All questions answered'
            : `${unansweredCount} left to answer`}
        </Text>
        <div className="flex gap-3">
          <Button
            variant="secondary"
            disabled={isLoading}
            onClick={handleSaveAndExit}
          >
            Save and exit
          </Button>
          <Button disabled={isLoading} onClick={handleSubmit}>
            {outstandingFormCount > 1 ? 'Next' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TaxfyleShortForm
